import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { apiRequestJson } from '../../api/apiService.js';

/**
 * @fileoverview Slice Redux para gerenciamento de produtos.
 * Gerencia estado de produtos, favoritos, carrinho, busca e filtros.
 * Favoritos e categorias são mutuamente exclusivos para evitar conflitos de interface.
 * 
 * @module ProductsSlice
 * @since 1.0.0
 */

/**
 * Thunk para buscar produtos da API.
 * Faz requisição para o endpoint de produtos e atualiza o estado.
 * 
 * @async
 * @function fetchProducts
 * @returns {Promise<Object>} Dados dos produtos da API
 */
export const fetchProducts = createAsyncThunk(
  'products/fetchProducts',
  async () => {
    return await apiRequestJson('products_list');
  }
);

/**
 * Thunk para buscar produtos de uma categoria específica.
 * Faz requisição para o endpoint de produtos por categoria.
 * 
 * @async
 * @function fetchProductsByCategory
 * @param {string} categoryName - Nome da categoria
 * @returns {Promise<Object>} Dados dos produtos da categoria
 */
export const fetchProductsByCategory = createAsyncThunk(
  'products/fetchProductsByCategory',
  async (categoryName) => {
    return await apiRequestJson('products_by_category', {
      params: { name: categoryName }
    });
  }
);

/**
 * Thunk para atualizar uma categoria.
 * Atualiza o status (enable) e a imagem da categoria.
 * 
 * @async
 * @function updateCategory
 * @param {Object} payload - Dados da atualização
 * @param {number} payload.id - ID da categoria
 * @param {boolean} payload.enable - Status da categoria
 * @param {string} [payload.category_image] - Imagem em base64
 * @returns {Promise<Object>} Categoria atualizada
 */
export const updateCategory = createAsyncThunk(
  'products/updateCategory',
  async ({ id, enable, category_image }) => {
    return await apiRequestJson('products_categories_update', {
      params: { id },
      body: { enable, category_image }
    });
  }
);

/**
 * Thunk para atualizar uma marca.
 * Atualiza o status (enable) e a imagem da marca.
 * 
 * @async
 * @function updateBrand
 * @param {Object} payload - Dados da atualização
 * @param {number} payload.id - ID da marca
 * @param {boolean} payload.enable - Status da marca
 * @param {string} [payload.brand_image] - Imagem em base64
 * @returns {Promise<Object>} Marca atualizada
 */
export const updateBrand = createAsyncThunk(
  'products/updateBrand',
  async ({ id, enable, brand_image }) => {
    return await apiRequestJson('products_brands_update', {
      params: { id },
      body: { enable, brand_image }
    });
  }
);

/**
 * Thunk para buscar produtos de uma marca específica.
 * Faz requisição para o endpoint de produtos por marca.
 * 
 * @async
 * @function fetchProductsByBrand
 * @param {string} brandName - Nome da marca
 * @returns {Promise<Object>} Dados dos produtos da marca
 */
export const fetchProductsByBrand = createAsyncThunk(
  'products/fetchProductsByBrand',
  async (brandName) => {
    return await apiRequestJson('products_by_brand', {
      params: { name: brandName }
    });
  }
);

const productsSlice = createSlice({
  name: 'products',
  initialState: {
    stateProducts: [],
    filteredProducts: [],
    categoryProducts: [],
    brandProducts: [],
    showFavorites: false,
    showCategories: false,
    showBrands: false,
    showPromotions: false,
    selectedCategory: null,
    lastViewedCategory: null,
    selectedBrand: null,
    lastViewedBrand: null,
    selectedPromotion: null,
    lastViewedPromotion: null,
    fromPromotion: false,
    searchQuery: '',
    loading: false,
    error: null,
  },
  reducers: {
    setSearchQuery: (state, action) => {
      state.searchQuery = action.payload;
      if (action.payload.trim() === '') {
        state.filteredProducts = [];
      } else {
        const query = action.payload.toLowerCase();
        // Busca sempre em TODOS os produtos disponíveis, não apenas nos exibidos
        state.filteredProducts = state.stateProducts.filter(product => 
          product.nome?.toLowerCase().includes(query) ||
          product.descricao?.toLowerCase().includes(query) ||
          product.marca?.toLowerCase().includes(query) ||
          product.categoria?.toLowerCase().includes(query)
        );
      }
    },
    selectProduct: (state, action) => {
      // Define o produto selecionado como termo de busca para exibir no workspace
      state.searchQuery = action.payload.nome;
      state.filteredProducts = [action.payload];
      // Limpa outros filtros para garantir que o produto seja exibido
      state.showFavorites = false;
      state.showCategories = false;
      state.showBrands = false;
      state.showPromotions = false;
      state.selectedCategory = null;
      state.selectedBrand = null;
      state.selectedPromotion = null;
    },
    clearSearch: (state) => {
      state.searchQuery = '';
      state.filteredProducts = [];
    },

    showFavorites: (state) => {
      state.showFavorites = true;
      state.showCategories = false;
      state.showBrands = false;
      state.showPromotions = false;
      state.selectedCategory = null;
      state.selectedBrand = null;
      state.selectedPromotion = null;
      state.searchQuery = '';
      state.filteredProducts = [];
    },
    showAllProducts: (state) => {
      state.showFavorites = false;
      state.showCategories = false;
      state.showBrands = false;
      state.showPromotions = false;
      state.selectedCategory = null;
      state.selectedBrand = null;
      state.selectedPromotion = null;
      state.categoryProducts = [];
      state.brandProducts = [];
    },
    selectCategory: (state, action) => {
      state.selectedCategory = action.payload;
      state.lastViewedCategory = action.payload;
      state.showFavorites = false;
      state.showCategories = false;
      if (!state.fromPromotion) {
        state.showPromotions = false;
        state.selectedPromotion = null;
      }
      state.searchQuery = '';
      state.filteredProducts = [];
    },
    /**
     * Limpa a categoria selecionada e volta para a visualização geral.
     * Remove produtos da categoria e reseta estados relacionados.
     * Mantém lastViewedCategory para scroll posterior.
     */
    clearSelectedCategory: (state) => {
      state.selectedCategory = null;
      state.categoryProducts = [];
      state.showFavorites = false;
      if (!state.fromPromotion) {
        state.selectedPromotion = null;
      }
    },
    /**
     * Ativa a visualização de categorias.
     * Desativa favoritos e limpa busca e produtos filtrados.
     */
    showCategories: (state) => {
      state.showCategories = true;
      state.showFavorites = false;
      state.showBrands = false;
      state.showPromotions = false;
      state.selectedCategory = null;
      state.selectedBrand = null;
      state.selectedPromotion = null;
      state.fromPromotion = false;
      state.searchQuery = '';
      state.filteredProducts = [];
    },
    /**
     * Seleciona uma marca e armazena para navegação contextual.
     * Desativa favoritos, categorias e limpa busca.
     */
    selectBrand: (state, action) => {
      state.selectedBrand = action.payload;
      state.lastViewedBrand = action.payload;
      state.showFavorites = false;
      state.showCategories = false;
      state.showBrands = false;
      if (!state.fromPromotion) {
        state.showPromotions = false;
        state.selectedPromotion = null;
      }
      state.selectedCategory = null;
      state.searchQuery = '';
      state.filteredProducts = [];
    },
    /**
     * Limpa a marca selecionada e volta para a visualização geral.
     * Remove produtos da marca e reseta estados relacionados.
     * Mantém lastViewedBrand para scroll posterior.
     */
    clearSelectedBrand: (state) => {
      state.selectedBrand = null;
      state.brandProducts = [];
      state.showFavorites = false;
      if (!state.fromPromotion) {
        state.selectedPromotion = null;
      }
    },
    /**
     * Ativa a visualização de marcas.
     * Desativa favoritos, categorias e limpa busca e produtos filtrados.
     */
    showBrands: (state) => {
      state.showBrands = true;
      state.showFavorites = false;
      state.showCategories = false;
      state.showPromotions = false;
      state.selectedCategory = null;
      state.selectedBrand = null;
      state.selectedPromotion = null;
      state.fromPromotion = false;
      state.searchQuery = '';
      state.filteredProducts = [];
    },
    /**
     * Ativa a visualização de promoções.
     * Desativa favoritos, categorias, marcas e limpa busca.
     */
    showPromotions: (state) => {
      state.showPromotions = true;
      state.showFavorites = false;
      state.showCategories = false;
      state.showBrands = false;
      state.selectedCategory = null;
      state.selectedBrand = null;
      state.selectedPromotion = null;
      state.searchQuery = '';
      state.filteredProducts = [];
    },
    /**
     * Seleciona uma promoção para visualizar detalhes.
     * Armazena lastViewedPromotion para scroll contextual.
     * Desativa outras visualizações.
     */
    selectPromotion: (state, action) => {
      state.selectedPromotion = action.payload;
      state.lastViewedPromotion = action.payload;
      state.fromPromotion = true;
      state.showPromotions = false;
      state.showFavorites = false;
      state.showCategories = false;
      state.showBrands = false;
      state.selectedCategory = null;
      state.selectedBrand = null;
    },
    /**
     * Limpa a promoção selecionada e volta para visualização de promoções.
     */
    clearSelectedPromotion: (state) => {
      state.selectedPromotion = null;
      state.fromPromotion = false;
      state.showPromotions = true;
    },
    /**
     * Carrega produtos do contexto global (localStorage).
     * Usado quando os dados já estão disponíveis localmente.
     */
    loadProductsFromContext: (state, action) => {
      state.stateProducts = action.payload || [];
      state.loading = false;
      state.error = null;
    },

  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchProducts.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchProducts.fulfilled, (state, action) => {
        state.loading = false;
        if (action.payload.success) {
          state.stateProducts = action.payload.data;
        } else {
          state.error = 'Erro ao carregar produtos';
        }
      })
      .addCase(fetchProducts.rejected, (state) => {
        state.loading = false;
        state.error = 'Erro de conexão com o servidor';
      })
      .addCase(fetchProductsByCategory.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchProductsByCategory.fulfilled, (state, action) => {
        state.loading = false;
        if (action.payload.success) {
          state.categoryProducts = action.payload.data;
        } else {
          state.error = action.payload.message || 'Erro ao carregar produtos da categoria';
          state.categoryProducts = [];
        }
      })
      .addCase(fetchProductsByCategory.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Erro de conexão com o servidor';
        state.categoryProducts = [];
      })
      .addCase(fetchProductsByBrand.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchProductsByBrand.fulfilled, (state, action) => {
        state.loading = false;
        if (action.payload.success) {
          state.brandProducts = action.payload.data;
        } else {
          state.error = action.payload.message || 'Erro ao carregar produtos da marca';
          state.brandProducts = [];
        }
      })
      .addCase(fetchProductsByBrand.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Erro de conexão com o servidor';
        state.brandProducts = [];
      });
  },
});

export const { setSearchQuery, selectProduct, clearSearch, showFavorites, showAllProducts, showCategories, selectCategory, clearSelectedCategory, selectBrand, clearSelectedBrand, showBrands, showPromotions, selectPromotion, clearSelectedPromotion, loadProductsFromContext } = productsSlice.actions;
export default productsSlice.reducer;